import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BadgeDollarSign,
  CalendarClock,
  CalendarRange,
  Palmtree,
  Plane,
  Plus,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  computePtoBalance,
  currentPtoYear,
  useCancelPtoRequest,
  useMyPtoAward,
  useMyPtoRequests,
} from '@/features/pto/hooks';
import {
  PTO_STATUS_BADGE,
  PTO_STATUS_LABELS,
  formatDays,
  formatLeaveDetail,
} from '@/features/pto/format';
import {
  calendarDaysUntil,
  formatISODateLocal,
  potentialPtoPayoutCents,
  ptoCycleEndDate,
  ptoPayoutDueDate,
} from '@/features/pto/cycle';
import { RequestPtoDialog } from '@/features/pto/RequestPtoDialog';
import { useMyHrInfo } from '@/features/hr/hooks';

/**
 * "My PTO" — the per-person self-service surface. Shows the user's annual
 * award, remaining balance, personal PTO cycle + unused-day payout estimate,
 * and a list of every PTO request they've made. See migrations 95 + 98.
 */
export function MyPtoPage() {
  const year = currentPtoYear();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: award, isLoading: awardLoading } = useMyPtoAward(year);
  const { data: requests, isLoading: requestsLoading } = useMyPtoRequests();
  const { data: hrInfo, isLoading: hrLoading } = useMyHrInfo();
  const cancel = useCancelPtoRequest();

  const balance = useMemo(
    () => computePtoBalance(year, award ?? null, requests ?? []),
    [year, award, requests],
  );

  const cycle = useMemo(() => {
    const startDate = hrInfo?.start_date ?? null;
    if (!startDate) {
      return {
        startDate: null as string | null,
        cycleEnd: null as Date | null,
        daysUntilEnd: null as number | null,
        payoutCents: null as number | null,
        payoutDue: null as Date | null,
      };
    }
    const cycleEnd = ptoCycleEndDate(startDate);
    const daysUntilEnd = calendarDaysUntil(cycleEnd);
    const payoutCents = potentialPtoPayoutCents(
      balance.remainingDays,
      hrInfo?.pay_rate_cents,
      hrInfo?.pay_type,
    );
    return {
      startDate,
      cycleEnd,
      daysUntilEnd,
      payoutCents,
      payoutDue: ptoPayoutDueDate(cycleEnd),
    };
  }, [hrInfo, balance.remainingDays]);

  const isLoading = awardLoading || requestsLoading || hrLoading;
  const hasAward = balance.totalDays > 0;

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this PTO request?')) return;
    try {
      await cancel.mutateAsync(id);
      toast.success('Request cancelled.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel.');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="My PTO"
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Request time off
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <BalanceTile
          label={`${year} award`}
          value={balance.totalDays}
          icon={Palmtree}
          description={
            balance.carryoverDays > 0
              ? `${formatDays(balance.awardedDays)} + ${formatDays(
                  balance.carryoverDays,
                )} carryover`
              : 'Days granted this year'
          }
        />
        <BalanceTile
          label="Taken"
          value={balance.usedDays}
          icon={Plane}
          description="Approved days used"
        />
        <BalanceTile
          label="Pending"
          value={balance.pendingDays}
          icon={CalendarClock}
          description="Awaiting approval"
          tone="info"
        />
        <BalanceTile
          label="Remaining"
          value={balance.remainingDays}
          icon={Palmtree}
          description="Award minus taken"
          tone="primary"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>PTO cycle & payout</CardTitle>
        </CardHeader>
        <CardContent>
          {hrLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : !cycle.startDate ? (
            <p className="text-sm text-muted-foreground">
              Employment start date not set yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <InfoTile
                label="Start date"
                value={formatDate(cycle.startDate)}
                description="Employment start"
                icon={CalendarRange}
              />
              <InfoTile
                label="PTO cycle end"
                value={
                  cycle.cycleEnd
                    ? formatDate(formatISODateLocal(cycle.cycleEnd))
                    : '—'
                }
                description="Next 12-month anniversary"
                icon={CalendarClock}
              />
              <InfoTile
                label="Days until cycle end"
                value={
                  cycle.daysUntilEnd != null ? String(cycle.daysUntilEnd) : '—'
                }
                description="Calendar days remaining"
                icon={CalendarRange}
              />
              <InfoTile
                label="Potential payout"
                value={
                  cycle.payoutCents != null
                    ? formatCurrency(cycle.payoutCents)
                    : '—'
                }
                description={
                  cycle.payoutCents == null
                    ? 'Pay rate not set — ask HR'
                    : cycle.payoutDue
                      ? `Before tax · paid within 30 days (by ${formatDate(
                          formatISODateLocal(cycle.payoutDue),
                        )})`
                      : 'Before tax · paid within 30 days of cycle end'
                }
                icon={BadgeDollarSign}
                emphasize
              />
            </div>
          )}
        </CardContent>
      </Card>

      {!isLoading && !hasAward && (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            You don&apos;t have a PTO award set for {year} yet. You can still
            submit requests — ask an admin to set your annual allotment so your
            balance shows correctly.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Time off history</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (requests ?? []).length === 0 ? (
            <EmptyState
              icon={Palmtree}
              title="No PTO requests yet"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dates</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Type / detail</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reviewed by</TableHead>
                  <TableHead aria-label="Actions" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(requests ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">
                      {r.start_date === r.end_date
                        ? formatDate(r.start_date)
                        : `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatDays(r.days)}
                    </TableCell>
                    <TableCell className="max-w-[16rem] truncate text-muted-foreground">
                      {formatLeaveDetail(r)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={PTO_STATUS_BADGE[r.status]}>
                        {PTO_STATUS_LABELS[r.status]}
                      </Badge>
                      {r.status === 'rejected' && r.review_note && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {r.review_note}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.reviewer?.full_name ?? r.reviewer?.email ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancel(r.id)}
                          disabled={cancel.isPending}
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RequestPtoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        remainingDays={hasAward ? balance.remainingDays : undefined}
      />
    </div>
  );
}

function BalanceTile({
  label,
  value,
  icon: Icon,
  description,
  tone = 'default',
}: {
  label: string;
  value: number;
  icon: typeof Palmtree;
  description: string;
  tone?: 'default' | 'info' | 'primary';
}) {
  const iconClass =
    tone === 'info'
      ? 'text-atmosphere-dark'
      : tone === 'primary'
        ? 'text-bright-moss-dark'
        : 'text-muted-foreground';
  return (
    <Card>
      <CardHeader>
        <CardDescription className="flex items-center gap-2">
          <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
          {label}
        </CardDescription>
        <CardTitle className="text-3xl tracking-headline tabular-nums">
          {formatDays(value)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function InfoTile({
  label,
  value,
  description,
  icon: Icon,
  emphasize = false,
}: {
  label: string;
  value: string;
  description: string;
  icon: typeof Palmtree;
  emphasize?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <p className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </p>
      <p
        className={
          emphasize
            ? 'text-2xl font-semibold tracking-headline tabular-nums text-bright-moss-dark'
            : 'text-lg font-semibold tabular-nums text-foreground'
        }
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
